// src/components/IdealCandidateView/components/ScheduleInterviewModal.jsx

import React, { useState, useMemo, useContext } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Checkbox,
    FormControlLabel, Alert, Grid, Box, Typography, InputAdornment, IconButton, useTheme, Paper, Stack
} from '@mui/material';
import {
    PeopleOutline as PeopleIcon, VideocamOutlined as VideocamOutlinedIcon,
    ChevronLeft, ChevronRight
} from '@mui/icons-material';
import { createCalendarEvent, listCalendarEvents, getHolidays } from '../../../ctapi/calendarService';
import SpinningCustomLogo from '../../Common/SpinningCustomLogo';
import { format, formatISO, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday, isSameDay, parseISO } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import ModalContext from '../../../shared/ModalContext/ModalContext';


const MiniCalendar = ({ onDateSelect, events = [], holidays = [] }) => {
    const theme = useTheme();
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());

    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    const dayNames = ["S", "M", "T", "W", "T", "F", "S"];

    const eventDates = useMemo(() => new Set(events.map(e => format(parseISO(e.start.dateTime), 'yyyy-MM-dd'))), [events]);
    const holidayDates = useMemo(() => new Set(holidays.map(h => h.start.date)), [holidays]);

    const handleDayClick = (day) => {
        setSelectedDate(day);
        onDateSelect(format(day, 'yyyy-MM-dd'));
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight={500}>{format(currentMonth, 'MMMM yyyy')}</Typography>
                <Box>
                    <IconButton size="small" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}><ChevronLeft /></IconButton>
                    <IconButton size="small" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}><ChevronRight /></IconButton>
                </Box>
            </Box>
            <Grid container spacing={0.5} textAlign="center">
                {dayNames.map(day => <Grid item xs={12 / 7} key={day}><Typography variant="caption" color="text.secondary">{day}</Typography></Grid>)}
                {days.map((day) => {
                    const fDay = format(day, 'yyyy-MM-dd');
                    const isSelected = isSameDay(day, selectedDate);
                    const isEventDay = eventDates.has(fDay);
                    const isHoliday = holidayDates.has(fDay);
                    return (
                        <Grid item xs={12 / 7} key={day.toString()}>
                            <IconButton
                                size="small"
                                onClick={() => handleDayClick(day)}
                                sx={{
                                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                                    color: isSelected ? 'primary.contrastText' : (isSameMonth(day, monthStart) ? 'text.primary' : 'text.disabled'),
                                    border: isHoliday ? `1px solid ${theme.palette.secondary.main}` : (isToday(day) ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent'),
                                    position: 'relative',
                                    '&:hover': { bgcolor: isSelected ? 'primary.dark' : 'action.hover' }
                                }}
                            >
                                <Typography variant="body2" component="span">{format(day, 'd')}</Typography>
                                {isEventDay && <Box sx={{ width: 4, height: 4, bgcolor: isSelected ? 'common.white' : 'secondary.main', borderRadius: '50%', position: 'absolute', bottom: 4 }} />}
                            </IconButton>
                        </Grid>
                    );
                })}
            </Grid>
        </Paper>
    );
};


export default function ScheduleInterviewModal({ open, onClose, candidate, jobTitle }) {
    const [isScheduling, setIsScheduling] = useState(false);
    const [addMeetLink, setAddMeetLink] = useState(true); // Default to true
    const [error, setError] = useState(null);
    const [dateValue, setDateValue] = useState(format(new Date(), 'yyyy-MM-dd'));
    const { openModal } = useContext(ModalContext);

    const { data: events } = useQuery({ queryKey: ['calendarEvents'], queryFn: listCalendarEvents, enabled: open });
    const { data: holidays } = useQuery({ queryKey: ['holidays'], queryFn: getHolidays, enabled: open });

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        const formData = new FormData(event.currentTarget);
        const time = formData.get('time');
        if (!dateValue || !time) {
            setError("Please select both a date and a time.");
            return;
        }
        const startDateTime = new Date(`${dateValue}T${time}`);
        const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);

        const eventDetails = {
            title: formData.get('title'),
            description: `Interview with ${candidate?.candidate.name} for ${jobTitle}.`,
            start_time: formatISO(startDateTime),
            end_time: formatISO(endDateTime),
            use_video_conference: addMeetLink, // Pass the correct flag
        };

        setIsScheduling(true);
        try {
            await createCalendarEvent(eventDetails);
            onClose(true); // Close modal on success
            openModal('MEETING_CREATION_SUCCESS');
        } catch (err) {
            setError(err.message || "An error occurred while scheduling the event.");
        } finally {
            setIsScheduling(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="md" PaperProps={{ component: 'form', onSubmit: handleSubmit, sx: { borderRadius: '24px' } }}>
            <DialogTitle sx={{ p: 3, pb: 2 }}><Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>Schedule Interview with {candidate?.candidate.name}</Typography></DialogTitle>
            <DialogContent sx={{ px: 3, pt: 1 }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <Stack spacing={2.5}>
                            <TextField autoFocus name="title" label="Event Title" fullWidth variant="filled" defaultValue={`Interview: ${candidate?.candidate.name} for ${jobTitle}`} required />
                            <TextField name="guests" label="Guests" fullWidth variant="filled" defaultValue={candidate?.candidate.email} InputProps={{ startAdornment: <InputAdornment position="start"><PeopleIcon /></InputAdornment> }} />
                            <FormControlLabel
                                control={<Checkbox checked={addMeetLink} onChange={(e) => setAddMeetLink(e.target.checked)}/>}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <VideocamOutlinedIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                        <Typography>Add Google Meet video conference</Typography>
                                    </Box>
                                }
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Stack spacing={2}>
                            <MiniCalendar onDateSelect={setDateValue} events={events} holidays={holidays} />
                            <TextField name="time" label="Time" type="time" fullWidth variant="filled" InputLabelProps={{ shrink: true }} required />
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                <Button onClick={() => onClose(false)} sx={{ textTransform: 'none', borderRadius: '12px' }}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={isScheduling} sx={{ textTransform: 'none', borderRadius: '12px', px: 3 }}>
                    {isScheduling ? <SpinningCustomLogo size={24} color="inherit" /> : 'Schedule'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}